import React, { useMemo } from "react";
import { Grid } from "@mui/material";
import DecoSelect from "./DecoSelect";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import GripOptions from "./GripOptions";
import {
  specialDoorTypes,
  specialDoorSizes,
  specialDoorHeights,
  specialDoorInserts,
  specialDoorFrames,
  specialDoorEdges,
  gripoptions,
  gripseriesoptions,
  gripsizeoptions,
  gripcoloroptions,
} from "../constants/closetOptions";

const SpecialDoorOptions = ({
  specialDoorType,
  setSpecialDoorType,
  specialDoorSize,
  setSpecialDoorSize,
  specialDoorHeight,
  setSpecialDoorHeight,
  specialDoorInsert,
  setSpecialDoorInsert,
  specialDoorFrame,
  setSpecialDoorFrame,
  specialDoorEdge,
  setSpecialDoorEdge,
  specialDoorFieldErrors,
  setSpecialDoorFieldErrors,
  specialDoorgripStyle,
  setSpecialDoorgripStyle,
  specialDoorgripSelectedSeries,
  setSpecialDoorgripSelectedSeries,
  specialDoorgripSelectedSize,
  setSpecialDoorgripSelectedSize,
  specialDoorgripColor,
  setSpecialDoorgripColor
}) => {
  const availableSizes = useMemo(
    () => specialDoorSizes[specialDoorType] || [],
    [specialDoorType]
  );
  const availableHeights = useMemo(
    () => specialDoorHeights[specialDoorType] || [],
    [specialDoorType]
  );
  const availableInserts = useMemo(
    () => specialDoorInserts[specialDoorType] || [],
    [specialDoorType]
  );
  const availableFrames = useMemo(
    () => specialDoorFrames[specialDoorType] || [],
    [specialDoorType]
  );
  const availableEdges = useMemo(
    () => specialDoorEdges[specialDoorType] || [],
    [specialDoorType]
  );

  return (
    <>
      {/* Special Door Type */}
      <Grid item xs={6}>
        <DecoSelect
          labelId="special-door-type-label"
          value={specialDoorType}
          onChange={(e) => {
            setSpecialDoorType(e.target.value);
            setSpecialDoorSize("");
            setSpecialDoorHeight("");
            setSpecialDoorInsert("");
            setSpecialDoorFrame("");
            setSpecialDoorEdge("");
            setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorType: false }));
            // ---- NEW: reset grip fields ----
            setSpecialDoorgripStyle("");
            setSpecialDoorgripSelectedSeries("");
            setSpecialDoorgripSelectedSize("");
            setSpecialDoorgripColor("");
          }}
          menuItems={specialDoorTypes}
          label="Special Door Type"
          isError={!specialDoorType || specialDoorFieldErrors?.specialDoorType}
        />
      </Grid>

      {/* Size */}
      {specialDoorType && availableSizes.length > 0 && (
        <Grid item xs={6}>
          <DecoSelect
            labelId="special-door-size-label"
            value={specialDoorSize}
            onChange={(e) => {
              setSpecialDoorSize(e.target.value);
              setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorSize: false }));
            }}
            isError={!specialDoorSize || specialDoorFieldErrors?.specialDoorSize}

            menuItems={availableSizes}
            label="Size"

          />
        </Grid>
      )}

      {/* Height */}
      {specialDoorType && availableHeights.length > 0 && (
        <Grid item xs={6}>
          <DecoSelect
            labelId="special-door-height-label"
            value={specialDoorHeight}
            onChange={(e) => {
              setSpecialDoorHeight(e.target.value);
              setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorHeight: false }));
            }}
            isError={!specialDoorHeight || specialDoorFieldErrors?.specialDoorHeight}

            menuItems={availableHeights}
            label="Height"

          />
        </Grid>
      )}

      {/* Insert */}
      {specialDoorType && availableInserts.length > 0 && (
        <Grid item xs={6}>
          <DecoSelect
            labelId="special-door-insert-label"
            value={specialDoorInsert}
            onChange={(e) => {
              setSpecialDoorInsert(e.target.value);
              setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorInsert: false }));
            }}
            isError={!specialDoorInsert || specialDoorFieldErrors?.specialDoorInsert}

            menuItems={availableInserts}
            label="Insert"

          />
        </Grid>
      )}

      {/* Frame */}
      {specialDoorType && availableFrames.length > 0 && (
        <Grid item xs={6}>
          <DecoSelect
            labelId="special-door-frame-label"
            value={specialDoorFrame}
            onChange={(e) => {
              setSpecialDoorFrame(e.target.value);
              setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorFrame: false }));
            }}
            isError={!specialDoorFrame || specialDoorFieldErrors?.specialDoorFrame}

            menuItems={availableFrames}
            label="Frame"
          //isError={!specialDoorFrame}
          />
        </Grid>
      )}

      {/* Edge */}
      {specialDoorType && availableEdges.length > 0 && (
        <Grid item xs={6}>
          <DecoSelect
            labelId="special-door-edge-label"
            value={specialDoorEdge}
            onChange={(e) => {
              setSpecialDoorEdge(e.target.value);
              setSpecialDoorFieldErrors((prev) => ({ ...prev, specialDoorEdge: false }));
            }}
            isError={!specialDoorEdge || specialDoorFieldErrors?.specialDoorEdge}

            menuItems={availableEdges}
            label="Edge"
          //isError={!specialDoorEdge}
          />
        </Grid>
      )}
      {/* ---- GRIP SECTION (only when TFL Doors selected) ---- */}
      {specialDoorType === "TFL Doors" && (
        <>
          {/* Grip Heading — full width */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Grip
            </Typography>
          </Grid>

          {/* Grip Type — required dropdown, same as standard door */}
          <Grid item xs={6}>
            <Autocomplete
              options={gripoptions}
              value={specialDoorgripStyle || null}
              onChange={(_, v) => {
                setSpecialDoorgripStyle(v || "");
                // reset downstream fields when grip type changes
                setSpecialDoorgripSelectedSeries("");
                setSpecialDoorgripSelectedSize("");
                setSpecialDoorgripColor("");
                setSpecialDoorFieldErrors((prev) => ({
                  ...prev,
                  specialDoorgripStyle: false,
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Grip Type"
                  fullWidth
                  error={
                    !specialDoorgripStyle ||
                    specialDoorFieldErrors?.specialDoorgripStyle
                  }
                />
              )}
            />
          </Grid>

          {/* Grip Series / Size / Color — reuses existing GripOptions component */}
          <GripOptions
            gripStyle={specialDoorgripStyle}
            selectedSeries={specialDoorgripSelectedSeries}
            setSelectedSeries={(val) => {
              setSpecialDoorgripSelectedSeries(val);
              setSpecialDoorgripSelectedSize("");
              setSpecialDoorgripColor("");
              setSpecialDoorFieldErrors((prev) => ({
                ...prev,
                specialDoorgripSelectedSeries: false,
              }));
            }}
            selectedSize={specialDoorgripSelectedSize}
            setSelectedSize={(val) => {
              setSpecialDoorgripSelectedSize(val);
              setSpecialDoorFieldErrors((prev) => ({
                ...prev,
                specialDoorgripSelectedSize: false,
              }));
            }}
            selectedColor={specialDoorgripColor}
            setSelectedColor={(val) => {
              setSpecialDoorgripColor(val);
              setSpecialDoorFieldErrors((prev) => ({
                ...prev,
                specialDoorgripColor: false,
              }));
            }}
            columns={2}
          />
        </>
      )}

    </>
  );
};

export default SpecialDoorOptions;
